import { api } from '../utils/api';

type DiagnosticLevel = 'error' | 'warning' | 'info';

interface DiagnosticData {
  component: string;
  error: string;
  context?: Record<string, unknown>;
  level?: DiagnosticLevel;
}

/**
 * Service for logging client-side diagnostic information to the server
 */
class DiagnosticService {
  private static isLoggingError = false; // Flag to prevent recursive logging

  /**
   * Log a diagnostic event to the server
   * @param data The diagnostic data to log
   * @returns Promise resolving to success status
   */
  public static async logEvent(data: DiagnosticData): Promise<boolean> {
    if (this.isLoggingError) {
      console.warn('Skipping recursive diagnostic logging');
      return false;
    }

    try {
      const { component, error, context = {}, level = 'error' } = data;

      // Add severity prefix to error message based on level
      const formattedError = level === 'error' 
        ? `[CRITICAL] ${error}` 
        : level === 'warning'
        ? `[WARNING] ${error}`
        : `[INFO] ${error}`;

      // Include browser information in context
      const enhancedContext = {
        ...context,
        url: window.location.href,
        userAgent: navigator.userAgent,
        protocol: window.location.protocol,
        timestamp: new Date().toISOString()
      };

      // Set the flag to indicate logging is in progress
      this.isLoggingError = true;

      // Send diagnostic data to server
      const response = await api.post<{ success: boolean }>('/api/auth/diagnostics', {
        component,
        error: formattedError,
        context: enhancedContext,
        timestamp: new Date().toISOString()
      });

      return response.success || false;
    } catch (error) {
      console.error('Failed to log diagnostic event:', error);
      return false;
    } finally {
      // Reset the flag after logging attempt
      this.isLoggingError = false;
    }
  }

  /**
   * Log a Turnstile-specific issue
   * @param errorType Type of Turnstile error
   * @param details Additional details about the error
   * @returns Promise resolving to success status
   */
  public static async logTurnstileIssue(errorType: string, details: Record<string, unknown> = {}): Promise<boolean> {
    return this.logEvent({
      component: 'TurnstileWidget',
      error: `Turnstile ${errorType}`,
      context: {
        ...details,
        turnstileAvailable: !!window.turnstile
      }
    });
  }

  /**
   * Log a network error
   * @param endpoint The API endpoint that failed
   * @param error The error that occurred
   * @param context Additional context
   * @returns Promise resolving to success status
   */
  public static async logNetworkError(endpoint: string, error: unknown, context: Record<string, unknown> = {}): Promise<boolean> {
    return this.logEvent({
      component: 'NetworkRequest',
      error: `API Error: ${endpoint}`,
      context: {
        ...context,
        endpoint,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      }
    });
  }
}

export default DiagnosticService;